import React from 'react';
import { createPortal } from 'react-dom';
import { X, Quote, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import api from '../api';

export default function LeaveDetailsModal({ isOpen, onClose, record, onUpdate }) {
  const [adminComment, setAdminComment] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  if (!isOpen || !record) return null;

  const handleStatus = async (status) => {
    try {
      setLoading(true);
      await api.put(`/admin/leaves/${record.id}`, { 
        status,
        admin_comment: adminComment 
      });
      toast.success(`Leave request ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      if (onUpdate) await onUpdate();
      onClose();
    } catch (err) {
      toast.error('Failed to update leave status.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Leave Request?',
      text: "This record will be permanently deleted.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await api.delete(`/admin/leaves/${record.id}`);
        toast.success('Leave request deleted successfully');
        if (onUpdate) await onUpdate();
        onClose();
      } catch (err) {
        toast.error('Failed to delete leave request.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      {/* Blurred background */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      ></div>
      
      {/* Modal Box */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-[420px] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {record.avatar ? (
              <img src={record.avatar} alt={record.name} className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-[13px] font-black border border-blue-100 shadow-sm tracking-widest">
                {record.initials || record.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h3 className="text-[17px] font-black text-slate-800 leading-tight tracking-tight">{record.name}</h3>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Leave Application Details</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors shadow-sm"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-4">
          
          {/* Top Stats Grid */}
          <div className="grid grid-cols-3 border border-slate-200 rounded-lg p-3 shadow-sm bg-white">
            <div className="flex flex-col items-center justify-center border-r border-slate-100 px-2">
              <span className="text-[9px] font-black uppercase text-slate-500 tracking-wider mb-1">TOTAL LEAVES</span>
              <span className="text-xl font-black text-blue-600">{record.totalHistory || 0}</span>
            </div>
            <div className="flex flex-col items-center justify-center border-r border-slate-100 px-2">
              <span className="text-[9px] font-black uppercase text-slate-500 tracking-wider mb-1">THIS MONTH</span>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-black text-emerald-600">{record.monthHistory || 0}</span>
                <span className="text-[11px] font-bold text-slate-400">/ 4</span>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center px-2">
              <span className="text-[9px] font-black uppercase text-slate-500 tracking-wider mb-1">DURATION</span>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-black text-slate-800">{record.days || 1}</span>
                <span className="text-[11px] font-bold text-slate-400">Days</span>
              </div>
            </div>
          </div>

          {/* Type & Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-slate-200 rounded-lg p-3 shadow-sm bg-white">
              <span className="text-[9px] font-black uppercase text-slate-500 tracking-wider block mb-1">LEAVE TYPE</span>
              <span className="text-sm font-bold text-slate-800">{record.type}</span>
            </div>
            <div className="border border-slate-200 rounded-lg p-3 shadow-sm bg-white overflow-hidden">
              <span className="text-[9px] font-black uppercase text-slate-500 tracking-wider block mb-1">DATES</span>
              <span className="text-[11px] sm:text-[12px] font-bold text-slate-800 tracking-tight whitespace-nowrap block truncate">
                {record.startDate} <span className="text-gray-400 font-normal">to</span> {record.endDate ? record.endDate.split(' ')[0] : ''}
              </span>
            </div>
          </div>

          {/* Detailed Reason */}
          <div>
            <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider block mb-1.5 ml-1">DETAILED REASON</span>
            <div className="border border-slate-200 rounded-lg p-4 shadow-sm bg-slate-50/50 relative overflow-hidden">
              <Quote className="absolute top-3 left-3 text-slate-200 rotate-180" size={24} />
              <p className="text-[13px] font-medium text-slate-600 italic relative z-10 pl-5 pr-2 leading-relaxed">
                {record.reason || "No reason provided."}
              </p>
            </div>
          </div>

          {/* Admin Remarks Input or Display */}
          {record.status === 'PENDING' ? (
            <div>
              <span className="text-[10px] font-black uppercase text-indigo-500 tracking-wider block mb-1.5 ml-1">ADMIN REMARKS / COMMENT</span>
              <textarea 
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                placeholder="Write your remarks here before approving/rejecting..."
                className="w-full h-20 border border-indigo-100 rounded-lg p-3 text-sm font-medium text-slate-700 bg-indigo-50/30 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 transition-all placeholder:text-slate-400 resize-none"
              />
            </div>
          ) : record.admin_comment && (
            <div>
              <span className="text-[10px] font-black uppercase text-indigo-500 tracking-wider block mb-1.5 ml-1">ADMIN'S RESPONSE / REMARKS</span>
              <div className="border border-indigo-100 rounded-lg p-4 shadow-sm bg-indigo-50/20 relative overflow-hidden">
                <Quote className="absolute top-3 left-3 text-indigo-200 rotate-180" size={24} />
                <p className="text-[13px] font-medium text-indigo-700 italic relative z-10 pl-5 pr-2 leading-relaxed">
                  {record.admin_comment}
                </p>
              </div>
            </div>
          )}

        </div>

        {/* Footer Buttons */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <button 
            onClick={handleDelete}
            disabled={loading}
            className="px-4 py-2 rounded-md border border-red-200 bg-white text-red-600 text-xs font-black uppercase tracking-wider shadow-sm hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading && <Loader2 size={12} className="animate-spin" />} Delete
          </button>
          
          {record.status === 'REJECTED' && (
            <button className="px-4 py-2 rounded-md border border-red-100 bg-red-100 text-red-700 text-xs font-black uppercase tracking-wider shadow-sm cursor-default">
              Currently Rejected
            </button>
          )}
          
          {record.status === 'APPROVED' && (
            <button className="px-4 py-2 rounded-md border border-emerald-100 bg-emerald-100 text-emerald-700 text-xs font-black uppercase tracking-wider shadow-sm cursor-default">
              Currently Approved
            </button>
          )}
          
          {record.status === 'PENDING' && (
            <div className="flex gap-2">
              <button 
                 onClick={() => handleStatus('rejected')}
                 disabled={loading}
                 className="px-4 py-2 rounded-md bg-red-500 text-white text-xs font-black uppercase tracking-wider shadow-sm hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                Reject
              </button>
              <button 
                 onClick={() => handleStatus('approved')}
                 disabled={loading}
                 className="px-4 py-2 rounded-md bg-emerald-500 text-white text-xs font-black uppercase tracking-wider shadow-sm hover:bg-emerald-600 transition-colors disabled:opacity-50"
              >
                Approve
              </button>
            </div>
          )}
        </div>

      </div>
    </div>,
    document.body
  );
}
